/**
 * ProviderProfilePage — detail view for a single provider.
 *
 * Loads the provider on mount and renders a header (name, status, activate /
 * deactivate actions) plus four tabs:
 *   Overview         — identity fields and schema assignment management
 *                      (add/remove limited to callers with can_manage)
 *   Known Mappings   — KnownMappingsPage scoped to this provider
 *   Ingestion History — HistoryPage scoped to this provider
 *   Users            — users linked via kodex_user_provider_access
 *
 * Optional banner query params from AddProviderPage:
 *   ?banner=draft      → "Saved as draft" info banner
 *   ?banner=activated  → "Provider activated" success banner
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, UserCircle, UserMinus } from "lucide-react";
import * as Tabs from "@radix-ui/react-tabs";
import { Button, AlertMessage, SkeletonPage, ConfirmDialog, Spinner } from "@/components/reusable";
import { ProviderHeader } from "@/components/providers/ProviderHeader";
import { ProviderIdentitySection } from "@/components/providers/ProviderIdentitySection";
import { ProviderSchemaAssignmentsSection } from "@/components/providers/ProviderSchemaAssignmentsSection";
import { ProviderDangerZoneSection } from "@/components/providers/ProviderDangerZoneSection";
import { KnownMappingsPage } from "@/pages/KnownMappingsPage";
import { HistoryPage } from "@/pages/HistoryPage";
import {
  fetchProvider,
  fetchProviderUsers,
  linkProviderUser,
  unlinkProviderUser,
} from "@/services/providersApi";
import { fetchAvailableUsers } from "@/services/meApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useRefreshCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";
import type { ProviderOut, ProviderUser } from "@/types/providers";
import type { PlatformUser } from "@/services/meApi";

// ── Overview Tab ───────────────────────────────────────────────────────────────

interface OverviewTabProps {
  provider: ProviderOut;
  isAdmin: boolean;
  onProviderUpdated: (updated: ProviderOut) => void;
}

/**
 * E4 — Overview tab: identity, schema assignments, and (when inactive) danger zone.
 * Each concern lives in its own section component for readability.
 */
function OverviewTab({ provider, isAdmin, onProviderUpdated }: OverviewTabProps) {
  return (
    <div className="flex flex-col gap-8">
      <ProviderIdentitySection
        provider={provider}
        isAdmin={isAdmin}
        onProviderUpdated={onProviderUpdated}
      />
      <ProviderSchemaAssignmentsSection
        provider={provider}
        isAdmin={isAdmin}
        onProviderUpdated={onProviderUpdated}
      />
      <ProviderDangerZoneSection provider={provider} isAdmin={isAdmin} />
    </div>
  );
}

// ── Users Tab ──────────────────────────────────────────────────────────────────

const ACCESS_ROLE_OPTIONS = [
  { value: "uploader", label: "Uploader" },
  { value: "admin", label: "Admin" },
] as const;

interface UsersTabProps {
  providerId: string;
  isAdmin: boolean;
}

/**
 * E7 / I5 — Users linked to this provider, with admin controls to add / remove.
 *
 * Admin view: searchable "Add user" form (GET /api/v2/users?not_linked_to=...) +
 *   access role selector + "Add" button (POST /api/v2/providers/{id}/users) +
 *   "Remove access" button per row with ConfirmDialog (DELETE).
 * Non-admin view: read-only table unchanged.
 */
function UsersTab({ providerId, isAdmin }: UsersTabProps) {
  const { track, trackError } = useTelemetry();
  const refreshCurrentUser = useRefreshCurrentUser();

  const [users, setUsers] = useState<ProviderUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-user form state
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<PlatformUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("uploader");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectingRef = useRef(false);

  // Remove confirm state
  const [removeConfirm, setRemoveConfirm] = useState<ProviderUser | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  function loadUsers() {
    return fetchProviderUsers(providerId)
      .then(setUsers)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : "Failed to load users.";
        setError(msg);
        trackError(err instanceof Error ? err : new Error(msg), { context: "users_tab_load" });
      });
  }

  useEffect(() => {
    loadUsers().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  // Debounced search for available users
  useEffect(() => {
    if (!isAdmin) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (selectingRef.current) {
      selectingRef.current = false;
      return;
    }

    setSelectedUserId("");
    if (!search.trim()) {
      setSearchResults([]);
      setAddError(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      fetchAvailableUsers(providerId, search.trim())
        .then((results) => {
          setSearchResults(results);
          setAddError(null);
        })
        .catch((err) => {
          setSearchResults([]);
          const msg = err instanceof Error ? err.message : "Failed to search users.";
          setAddError(msg);
          trackError(err instanceof Error ? err : new Error(msg), {
            context: "users_tab_search",
          });
        })
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, providerId, isAdmin, trackError]);

  async function handleAddUser() {
    if (!selectedUserId) return;
    setAdding(true);
    setAddError(null);
    try {
      await linkProviderUser(providerId, selectedUserId, selectedRole);
      track("provider_user_linked", { providerId, userId: selectedUserId, role: selectedRole });
      // Refresh /me after any link/unlink — cheap, and keeps ProviderSelector
      // current when the actor links/unlinks themselves.
      try {
        await refreshCurrentUser();
      } catch (err) {
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "users_tab_refresh_after_link",
        });
      }
      setSearch("");
      setSearchResults([]);
      setSelectedUserId("");
      setSelectedRole("uploader");
      await loadUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to add user.";
      setAddError(msg);
      trackError(err instanceof Error ? err : new Error(msg), { context: "add_provider_user" });
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveUser() {
    if (!removeConfirm) return;
    setRemovingId(removeConfirm.user_id);
    try {
      await unlinkProviderUser(providerId, removeConfirm.user_id);
      track("provider_user_unlinked", { providerId, userId: removeConfirm.user_id });
      try {
        await refreshCurrentUser();
      } catch (err) {
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "users_tab_refresh_after_unlink",
        });
      }
      setUsers((prev) => prev.filter((u) => u.user_id !== removeConfirm.user_id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to remove user.";
      trackError(err instanceof Error ? err : new Error(msg), { context: "remove_provider_user" });
    } finally {
      setRemovingId(null);
      setRemoveConfirm(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] py-8">
        <Spinner size="sm" />
        Loading users…
      </div>
    );
  }

  if (error) {
    return <AlertMessage variant="error" message={error} />;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Add user form — admin only */}
      {isAdmin && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-text-tertiary)]">
            Add User
          </h2>
          {addError && (
            <div className="mb-3">
              <AlertMessage variant="error" message={addError} />
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {/* Email search */}
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                Search by email
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type an email address…"
                  disabled={adding}
                  className="h-9 w-full rounded-md border border-[var(--color-border)] bg-white px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)] disabled:opacity-50"
                />
                {searchLoading && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>
              {/* Search results dropdown */}
              {searchResults.length > 0 && (
                <div className="mt-1 rounded-md border border-[var(--color-border-light)] bg-white shadow-sm overflow-hidden">
                  {searchResults.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => {
                        selectingRef.current = true;
                        setSelectedUserId(u.user_id);
                        setSearch(u.email ?? u.user_id);
                        setSearchResults([]);
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--color-bg-secondary)]",
                        selectedUserId === u.user_id && "bg-[var(--color-bg-secondary)]"
                      )}
                    >
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {u.email ?? u.user_id}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {search.trim() && !searchLoading && searchResults.length === 0 && !selectedUserId && (
                <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                  No registered users found. The user must log in at least once before they can be
                  linked.
                </p>
              )}
            </div>

            {/* Role selector */}
            <div className="sm:w-40">
              <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                Access role
              </label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={adding}
                className="h-9 w-full rounded-md border border-[var(--color-border)] bg-white px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)] disabled:opacity-50"
              >
                {ACCESS_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Add button */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddUser}
              disabled={!selectedUserId || adding}
              className="sm:self-end"
            >
              <Plus className="h-4 w-4" />
              {adding ? "Adding…" : "Add"}
            </Button>
          </div>
        </section>
      )}

      {/* Users table */}
      {users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserCircle className="mb-3 h-10 w-10 text-[var(--color-text-tertiary)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            No users linked to this provider yet.
          </p>
          {isAdmin && (
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
              Search for a registered user above to add them.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-secondary)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Email
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  User ID (Entra OID)
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Access Role
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Status
                </th>
                {isAdmin && <th className="px-4 py-2.5 w-12" />}
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr
                  key={user.user_id}
                  className={cn(
                    "transition-colors",
                    idx > 0 && "border-t border-[var(--color-border-light)]"
                  )}
                >
                  <td className="px-4 py-2.5 text-[var(--color-text-primary)]">
                    {user.email ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--color-text-secondary)]">
                    {user.user_id}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)] capitalize">
                    {user.access_role ?? "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                        user.is_active
                          ? "bg-green-50 border-green-200 text-green-700"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                      )}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setRemoveConfirm(user)}
                        disabled={removingId === user.user_id}
                        className="p-1.5 rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-error)] hover:bg-red-50 transition-colors disabled:opacity-40"
                        aria-label={`Remove access for ${user.email ?? user.user_id}`}
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Remove access confirmation */}
      <ConfirmDialog
        open={!!removeConfirm}
        onOpenChange={(open) => {
          if (!open && !removingId) setRemoveConfirm(null);
        }}
        title="Remove user access?"
        description={`Remove access for ${removeConfirm?.email ?? removeConfirm?.user_id} from this provider? They will no longer be able to submit files against it. This can be reversed by re-adding them.`}
        confirmLabel="Remove access"
        confirmLoadingLabel="Removing…"
        confirmLoading={!!removingId}
        tone="warning"
        onConfirm={handleRemoveUser}
      />
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function ProviderProfilePage() {
  const { providerId } = useParams<{ providerId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { track, trackError } = useTelemetry();

  const [provider, setProvider] = useState<ProviderOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Banner from query param (set by AddProviderPage after creation)
  const bannerParam = searchParams.get("banner");
  const bannerConfig =
    bannerParam === "draft"
      ? {
          variant: "info" as const,
          message: "Provider saved as draft. You can activate it when it's ready.",
        }
      : bannerParam === "activated"
        ? { variant: "success" as const, message: "Provider activated successfully." }
        : null;
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Load provider on mount
  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    fetchProvider(providerId)
      .then((p) => {
        setProvider(p);
        track("provider_profile_viewed", { providerId });
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : "Failed to load provider.";
        setLoadError(msg);
        trackError(err instanceof Error ? err : new Error(msg), {
          context: "provider_profile_load",
        });
      })
      .finally(() => setLoading(false));
  }, [providerId, track, trackError]);

  const handleStatusChanged = useCallback((updated: ProviderOut) => {
    setProvider(updated);
  }, []);

  const handleProviderUpdated = useCallback((updated: ProviderOut) => {
    setProvider(updated);
  }, []);

  if (loading) return <SkeletonPage />;

  if (loadError || !provider) {
    return (
      <div className="p-6">
        <AlertMessage variant="error" message={loadError ?? "Provider not found."} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Back navigation */}
      <button
        type="button"
        onClick={() => navigate("/provider-management/providers")}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Providers
      </button>

      {/* Creation banner (draft / activated) */}
      {bannerConfig && !bannerDismissed && (
        <div className="mb-4">
          <AlertMessage variant={bannerConfig.variant} message={bannerConfig.message} />
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="mt-1 text-xs text-[var(--color-text-tertiary)] hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* E1 — Persistent header (status badge + admin action button) */}
      <ProviderHeader provider={provider} onStatusChanged={handleStatusChanged} />

      {/* Tabs */}
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className="mb-6 flex gap-0 border-b border-[var(--color-border-light)]">
          {[
            { value: "overview", label: "Overview" },
            { value: "known-mappings", label: "Known Mappings" },
            { value: "history", label: "Ingestion History" },
            { value: "users", label: "Users" },
          ].map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              value={tab.value}
              className={cn(
                "relative px-5 py-3 text-sm font-medium transition-colors outline-none",
                "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
                "data-[state=active]:text-[var(--color-text-primary)]",
                "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-[var(--color-action-primary)] after:transition-opacity",
                "data-[state=inactive]:after:opacity-0 data-[state=active]:after:opacity-100"
              )}
            >
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* E4 — Overview tab */}
        <Tabs.Content value="overview" className="animate-fade-in">
          <OverviewTab
            provider={provider}
            isAdmin={provider.can_manage}
            onProviderUpdated={handleProviderUpdated}
          />
        </Tabs.Content>

        {/* Known Mappings tab: server-scoped to this provider only */}
        <Tabs.Content value="known-mappings" className="animate-fade-in">
          <KnownMappingsPage providerId={provider.provider_id} />
        </Tabs.Content>

        {/* Ingestion History tab — server-scoped to this provider only */}
        <Tabs.Content value="history" className="animate-fade-in">
          <HistoryPage providerId={provider.provider_id} providerName={provider.provider_name} />
        </Tabs.Content>

        {/* E7 / I5 — Users tab */}
        <Tabs.Content value="users" className="animate-fade-in">
          <UsersTab providerId={provider.provider_id} isAdmin={provider.can_manage} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
